import * as React from "react";
import { Button, Upload, Spin } from "antd";
import Text from "antd/lib/typography/Text";
import SignatureStepCard from "./SignatureStepCard";
import { TTranslate } from "../../../service/locale/TranslationService";
import { pdf } from "@react-pdf/renderer";
import Report1Template from "../report/report1Components/Report1Template";
import { IFormSectionPresentationModel } from "../../../presenter/form/model/FormSectionPresentationModel";
import { IReport1Applicant } from "../../../model/report/Report1Applicant";
import { IFormVersionItem } from "../../../model/report/FormVersionItem";
import { IFormTypeItem } from "../../../model/report/FormTypeItem";

export interface Props {
  translate: TTranslate;
  onFileSelected: (file: File | null) => void;
  generatePdfReport?: () => void;
  pdfReportData?: IFormSectionPresentationModel[];
  pdfReportLoader?: boolean;
  applicant?: IReport1Applicant;
  formVersion?: IFormVersionItem;
  formType?: IFormTypeItem;
  reportingPeriodId?: number;
  reportingPeriodFormTypeId?: number;
  isPortal?: boolean;
  fileName?: string;
  isRequired?: boolean;
  showError?: boolean;
}

export interface State {
  uploadedFile: any;
  isDownloading: boolean;
}

export default class SignatureSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      uploadedFile: null,
      isDownloading: false
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { pdfReportData } = this.props;
    const { isDownloading } = this.state;

    if (
      isDownloading &&
      pdfReportData &&
      pdfReportData !== prevProps.pdfReportData
    ) {
      this.triggerDownload();
    }
  }

  reset() {
    this.setState({
      uploadedFile: null,
      isDownloading: false
    });
  }

  triggerDownload = async () => {
    const {
      translate,
      pdfReportData,
      applicant,
      formVersion,
      formType,
      reportingPeriodId,
      reportingPeriodFormTypeId,
      isPortal = false,
      fileName
    } = this.props;

    try {
      const blob = await pdf(
        <Report1Template
          translate={translate}
          data={pdfReportData}
          title={translate("reports.report_1")}
          loader={false}
          applicant={applicant}
          formVersion={formVersion}
          formType={formType}
          reportingPeriodId={reportingPeriodId}
          reportingPeriodFormTypeId={reportingPeriodFormTypeId}
          isPortal={isPortal}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || `${translate("reports.report_1")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.setState({ isDownloading: false });
    } catch (e) {
      this.setState({ isDownloading: false });
    }
  };

  handleDownloadDocument = () => {
    const { generatePdfReport } = this.props;
    if (generatePdfReport) {
      this.setState({ isDownloading: true });
      generatePdfReport();
    }
  };

  handleFileUpload = (data: any) => {
    const { file, fileList } = data;
    if (file.status === "removed") {
      this.setState({ uploadedFile: null });
      this.props.onFileSelected(null);
      return;
    }
    const latestFile = fileList[fileList.length - 1];
    if (latestFile) {
      this.setState({ uploadedFile: latestFile });
      this.props.onFileSelected(latestFile.originFileObj);
    }
  };

  render() {
    const { translate, pdfReportLoader, isRequired, showError } = this.props;
    const { uploadedFile, isDownloading } = this.state;

    return (
      <Spin spinning={pdfReportLoader || false}>
        <h3 className={"display-form-section-title"}>
          {translate("reports.signature.section_title")}
          {isRequired && (
            <Text type="danger" style={{ fontSize: 12, marginLeft: 8 }}>
              *
            </Text>
          )}
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <SignatureStepCard
            stepNumber={1}
            title={translate("reports.signature.download_step_title")}
            description={translate(
              "reports.signature.download_step_description"
            )}
          >
            <Button
              type="primary"
              icon={isDownloading ? "loading" : "download"}
              block
              loading={isDownloading}
              onClick={this.handleDownloadDocument}
            >
              {translate("reports.signature.download_button")}
            </Button>
          </SignatureStepCard>

          <SignatureStepCard
            stepNumber={2}
            title={translate("reports.signature.upload_step_title")}
            description={translate("reports.signature.upload_step_description")}
          >
            <div>
              <Text style={{ fontWeight: 500, fontSize: 14 }}>
                {translate("reports.signature.upload_label")}
              </Text>
              <div style={{ marginTop: 8 }}>
                <Upload
                  beforeUpload={() => false}
                  onChange={this.handleFileUpload}
                  accept=".pdf"
                  fileList={uploadedFile ? [uploadedFile] : []}
                  type="drag"
                >
                  <div>{translate("reports.signature.upload_placeholder")}</div>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                    {translate("reports.signature.upload_allowed_formats", {
                      fileExtensions: "pdf"
                    })}
                    .
                  </div>
                </Upload>
                {showError && (
                  <Text
                    type="danger"
                    style={{ fontSize: 12, marginTop: 4, display: "block" }}
                  >
                    {translate("form_details.signed_report_required_message")}
                  </Text>
                )}
              </div>
            </div>
          </SignatureStepCard>
        </div>
      </Spin>
    );
  }
}
